import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// 主题配置接口
interface ThemeConfig {
  primary: string
  primaryLight: string
  primaryDark: string
  success: string
  warning: string
  danger: string
  bgPrimary: string
  bgSecondary: string
  bgWhite: string
  textPrimary: string
  textRegular: string
  textSecondary: string
}

// 默认主题
const defaultTheme: ThemeConfig = {
  primary: '#39ded7',
  primaryLight: '#7bece7',
  primaryDark: '#12d4cc',
  success: '#67C23A',
  warning: '#E6A23C',
  danger: '#F56C6C',
  bgPrimary: '#F0F8FF',
  bgSecondary: '#F5F7FA',
  bgWhite: '#FFFFFF',
  textPrimary: '#303133',
  textRegular: '#606266',
  textSecondary: '#909399'
}

export const useThemeStore = defineStore('theme', () => {
  // State
  const theme = ref<ThemeConfig>({ ...defaultTheme })
  const isGrayMode = ref(false)
  const isDarkMode = ref(false)

  // Getters
  const cssVars = computed(() => {
    const vars: Record<string, string> = {
      '--primary': theme.value.primary,
      '--primary-light': theme.value.primaryLight,
      '--primary-dark': theme.value.primaryDark,
      '--success': theme.value.success,
      '--warning': theme.value.warning,
      '--danger': theme.value.danger,
      '--bg-primary': theme.value.bgPrimary,
      '--bg-secondary': theme.value.bgSecondary,
      '--bg-white': theme.value.bgWhite,
      '--text-primary': theme.value.textPrimary,
      '--text-regular': theme.value.textRegular,
      '--text-secondary': theme.value.textSecondary
    }
    
    // 转换为 CSS 变量字符串
    return Object.entries(vars).map(([key, value]) => `${key}: ${value}`).join(';')
  })

  // Actions
  const initTheme = () => {
    // 从本地缓存读取主题配置
    const savedTheme = uni.getStorageSync('themeConfig') as ThemeConfig | null
    if (savedTheme) {
      theme.value = savedTheme
    }
    
    // 读取灰度模式
    const savedGrayMode = uni.getStorageSync('grayMode') as boolean
    if (savedGrayMode !== undefined) {
      isGrayMode.value = savedGrayMode
      applyGrayMode(savedGrayMode)
    }
    
    // 读取暗黑模式
    const savedDarkMode = uni.getStorageSync('darkMode') as boolean
    if (savedDarkMode !== undefined) {
      isDarkMode.value = savedDarkMode
    }
  }

  const setTheme = (newTheme: Partial<ThemeConfig>) => {
    theme.value = { ...theme.value, ...newTheme }
    uni.setStorageSync('themeConfig', theme.value)
  }

  const setGrayMode = (enabled: boolean) => {
    isGrayMode.value = enabled
    uni.setStorageSync('grayMode', enabled)
    applyGrayMode(enabled)
  }

  const applyGrayMode = (enabled: boolean) => {
    // #ifdef H5
    const body = document.body
    if (enabled) {
      body.classList.add('gray-mode')
    } else {
      body.classList.remove('gray-mode')
    }
    // #endif
    
    // 小程序和 App 通过全局样式控制
    if (enabled) {
      theme.value = {
        ...theme.value,
        primary: '#999999',
        primaryLight: '#aaaaaa',
        primaryDark: '#888888'
      }
    } else {
      theme.value = { ...defaultTheme }
    }
  }

  const setDarkMode = (enabled: boolean) => {
    isDarkMode.value = enabled
    uni.setStorageSync('darkMode', enabled)
    
    if (enabled) {
      theme.value = {
        ...theme.value,
        bgPrimary: '#1a1a1a',
        bgSecondary: '#2c2c2c',
        bgWhite: '#1a1a1a',
        textPrimary: '#ffffff',
        textRegular: '#cccccc',
        textSecondary: '#999999'
      }
    } else {
      theme.value = { ...defaultTheme }
    }
  }

  const resetTheme = () => {
    theme.value = { ...defaultTheme }
    isGrayMode.value = false
    isDarkMode.value = false
    uni.removeStorageSync('themeConfig')
    uni.removeStorageSync('grayMode')
    uni.removeStorageSync('darkMode')
    applyGrayMode(false)
  }

  return {
    theme,
    isGrayMode,
    isDarkMode,
    cssVars,
    initTheme,
    setTheme,
    setGrayMode,
    setDarkMode,
    resetTheme
  }
})
